import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import LikeIcon from '@atlaskit/icon/core/migration/thumbs-up--like';
import { token } from '@atlaskit/tokens';
import { xcss, Inline, Flex } from '@atlaskit/primitives';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import type { FeedbackCollectorProps } from '@atlassian/jira-feedback-collector/src/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useSmartSummarizeCommentsAnalytics } from '@atlassian/jira-issue-smart-request-summary-state/src/common/analytics/index.tsx';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { useIsProactivePanelShown } from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import { useUserPreferencesValue } from '@atlassian/jira-issue-user-preference-services/src/main.tsx';
import { PROACTIVE_COMMENT_SUMMARY_ENABLED } from '@atlassian/jira-issue-shared-types/src/common/types/user-preferences-type.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import messages from './messages.tsx';

type ButtonClickHandler = NonNullable<ButtonProps['onClick']>;

const getFeedbackContext = (
	sentiment: string,
	projectType = '',
	isProactive: boolean,
): FeedbackCollectorProps => ({
	entrypointId: 'a0b04376-5026-4a3a-92b3-d2ff97ae92c3',
	feedbackCollectorId: 'ddac4251-3a49-462d-8a6b-f602fe8ac06a',
	feedbackContext: [
		{ id: 'sentiment', value: sentiment },
		{ id: 'product', value: projectType },
		{ id: 'isProactiveExperience', value: isProactive },
	],
});

export const SummaryFeedbackCollectorOld = () => {
	const { formatMessage } = useIntl();
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const [isProactivePanelShown] = useIsProactivePanelShown();
	const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);

	const onClickHelpful = useCallback<ButtonClickHandler>(() => {
		fireTrack('aiFeedback submitted', 'feedbackSubmitted', {
			aiFeedbackResult: 'up',
		});
		openFeedbackCollector(getFeedbackContext('good', projectType, isProactivePanelShown));
		setHasSubmittedFeedback(true);
	}, [openFeedbackCollector, projectType, fireTrack, isProactivePanelShown]);

	const onClickUnhelpful = useCallback<ButtonClickHandler>(() => {
		fireTrack('aiFeedback submitted', 'feedbackSubmitted', {
			aiFeedbackResult: 'down',
		});

		openFeedbackCollector(getFeedbackContext('bad', projectType, isProactivePanelShown));
		setHasSubmittedFeedback(true);
	}, [openFeedbackCollector, projectType, fireTrack, isProactivePanelShown]);

	if (hasSubmittedFeedback) {
		return (
			<Container>
				<Heading size="xxsmall" as="div">
					{formatMessage(messages.thanks)}
				</Heading>
			</Container>
		);
	}

	return (
		<Container>
			<Button
				testId="issue-smart-request-summary.ui.footer.feedback.button-like"
				iconBefore={
					<LikeIcon
						LEGACY_size="small"
						label={formatMessage(messages.helpfulLabel)}
						color={token('color.text.subtlest')}
					/>
				}
				spacing="compact"
				onClick={onClickHelpful}
				appearance="subtle"
			/>
			<Button
				testId="issue-smart-request-summary.ui.footer.feedback.button-dislike"
				iconBefore={
					<DislikeIconWrapper>
						<LikeIcon
							LEGACY_size="small"
							label={formatMessage(messages.unhelpfulLabel)}
							color={token('color.text.subtlest')}
						/>
					</DislikeIconWrapper>
				}
				spacing="compact"
				onClick={onClickUnhelpful}
				appearance="subtle"
			/>
		</Container>
	);
};

export const SummaryFeedbackCollectorNew = () => {
	const { formatMessage } = useIntl();
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const { fireTrack } = useSmartSummarizeCommentsAnalytics();
	const [isProactivePanelShown] = useIsProactivePanelShown();
	const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);
	const [hasToggledFeatureSetting, setHasToggledFeatureSetting] = useState(false);
	const [hasEnabledProactiveFeature, { setValue }] = useUserPreferencesValue(
		PROACTIVE_COMMENT_SUMMARY_ENABLED,
	);

	const setFeatureToggle = useCallback(
		(value: string) => {
			setValue(value);
			setHasToggledFeatureSetting(true);
			fireTrack('button clicked', 'proactiveFeatureEnabled', {
				toggle: value,
			});
		},
		[fireTrack, setValue, setHasToggledFeatureSetting],
	);

	const onClickHelpful = useCallback<ButtonClickHandler>(() => {
		fireTrack('aiFeedback submitted', 'feedbackSubmitted', {
			aiFeedbackResult: 'up',
		});
		openFeedbackCollector(getFeedbackContext('good', projectType, isProactivePanelShown));
		setHasSubmittedFeedback(true);
	}, [openFeedbackCollector, projectType, fireTrack, isProactivePanelShown]);

	const onClickUnhelpful = useCallback<ButtonClickHandler>(() => {
		fireTrack('aiFeedback submitted', 'feedbackSubmitted', {
			aiFeedbackResult: 'down',
		});

		openFeedbackCollector(getFeedbackContext('bad', projectType, isProactivePanelShown));
		setHasSubmittedFeedback(true);
	}, [openFeedbackCollector, projectType, fireTrack, isProactivePanelShown]);

	const renderFeedbackButton = () => {
		if (hasSubmittedFeedback) {
			return (
				<Flex alignItems="center" gap="space.050">
					<Heading size="xxsmall" as="div">
						{formatMessage(messages.thanks)}
					</Heading>
				</Flex>
			);
		}

		return (
			<Flex alignItems="center" gap="space.050">
				<Button
					testId="issue-smart-request-summary.ui.footer.feedback.button-like"
					iconBefore={
						<LikeIcon
							LEGACY_size="small"
							label={formatMessage(messages.helpfulLabel)}
							color={token('color.text.subtlest')}
						/>
					}
					spacing="compact"
					onClick={onClickHelpful}
					appearance="subtle"
				/>
				<Button
					testId="issue-smart-request-summary.ui.footer.feedback.button-dislike"
					iconBefore={
						<DislikeIconWrapper>
							<LikeIcon
								LEGACY_size="small"
								label={formatMessage(messages.unhelpfulLabel)}
								color={token('color.text.subtlest')}
							/>
						</DislikeIconWrapper>
					}
					spacing="compact"
					onClick={onClickUnhelpful}
					appearance="subtle"
				/>
			</Flex>
		);
	};

	const renderProactiveFeatureToggle = () => {
		if (hasToggledFeatureSetting) {
			return (
				<Flex alignItems="center" gap="space.050">
					<Heading size="xxsmall" as="div">
						{hasEnabledProactiveFeature === 'true'
							? formatMessage(messages.autoSummarizeEnabledConfirmation)
							: formatMessage(messages.autoSummarizeDisabledConfirmation)}
					</Heading>
				</Flex>
			);
		}

		// we have proactively shown the proactiveness to users
		// which means they haven't yet disabled the feature
		// so we show the option to disable if they have expanded the proactive comp
		if (isProactivePanelShown) {
			return (
				<Flex alignItems="center" gap="space.050">
					<Button appearance="link" spacing="none" onClick={() => setFeatureToggle('false')}>
						{formatMessage(messages.turnOffAutoSummarize)}
					</Button>
				</Flex>
			);
		}

		// the user has toggled it off, so we give them ability to turn it back on
		// if they ever use the non-proactive feature again
		if (hasEnabledProactiveFeature === 'false') {
			return (
				<Flex alignItems="center" gap="space.050">
					<Button appearance="link" spacing="none" onClick={() => setFeatureToggle('true')}>
						{formatMessage(messages.turnOnAutoSummarize)}
					</Button>
				</Flex>
			);
		}

		return null;
	};

	return (
		<Inline xcss={containerStyle} spread="space-between" alignBlock="center">
			{renderFeedbackButton()}
			{renderProactiveFeatureToggle()}
		</Inline>
	);
};

export const SummaryFeedbackCollector = componentWithFG(
	'proactive_comment_summary_targeting_gate',
	SummaryFeedbackCollectorNew,
	SummaryFeedbackCollectorOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

const containerStyle = xcss({
	paddingInline: 'space.150',
	paddingBlock: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const DislikeIconWrapper = styled.div({
	transform: 'rotate(180deg)',
});
